import React, { useState } from 'react';
import { sectionLabelClass } from '../ChallengeWire/challengeWireFormStyles';
import {
	aiTrainerMutedTextClassName,
	aiTrainerPrimaryButtonClassName,
	aiTrainerSecondaryButtonClassName
} from './aiTrainerStyles';

function formatPlanForCopy(plan) {
	const lines = [plan.title];
	if (plan.summary) lines.push(plan.summary, '');

	plan.days?.forEach((day) => {
		lines.push(day.label);
		if (day.focus) lines.push(day.focus);
		day.exercises?.forEach((exercise) => {
			let line = `• ${exercise.name}`;
			if (exercise.sets_reps) line += ` — ${exercise.sets_reps}`;
			if (exercise.notes) line += ` (${exercise.notes})`;
			lines.push(line);
		});
		lines.push('');
	});

	if (plan.notes) {
		lines.push(plan.notes);
	}

	return lines.join('\n').trim();
}

export default function WorkoutPlanCard({ plan, embedded = false }) {
	const [expanded, setExpanded] = useState(true);
	const [copied, setCopied] = useState(false);

	if (!plan?.title || !plan?.days?.length) return null;

	async function handleCopy() {
		try {
			await navigator.clipboard.writeText(formatPlanForCopy(plan));
			setCopied(true);
			window.setTimeout(() => setCopied(false), 2000);
		} catch {
			setCopied(false);
		}
	}

	return (
		<div
			className={
				embedded
					? 'overflow-hidden'
					: 'rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card shadow-sm overflow-hidden'
			}
		>
			<div
				className={
					embedded
						? 'pb-3'
						: 'border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-bg px-4 py-3'
				}
			>
				{!embedded && <p className={sectionLabelClass}>Workout plan</p>}
				<p className={`${embedded ? '' : 'mt-1 '}text-base font-semibold text-gray-900 dark:text-dark-text`}>
					{plan.title}
				</p>
				{plan.summary && (
					<p className={`mt-1 text-sm ${aiTrainerMutedTextClassName}`}>{plan.summary}</p>
				)}
			</div>

			<div className={embedded ? 'space-y-4' : 'px-4 py-3 space-y-4'}>
				{(expanded ? plan.days : plan.days.slice(0, 1)).map((day) => (
					<div key={day.label}>
						<p className="text-sm font-semibold text-gray-900 dark:text-dark-text">{day.label}</p>
						{day.focus && (
							<p className={`mt-0.5 text-xs ${aiTrainerMutedTextClassName}`}>{day.focus}</p>
						)}
						<ul className="mt-2 space-y-2">
							{day.exercises?.map((exercise) => (
								<li
									key={`${day.label}-${exercise.name}`}
									className="rounded-lg border border-gray-100 dark:border-dark-border bg-gray-50 dark:bg-dark-bg px-3 py-2"
								>
									<p className="text-sm font-medium text-gray-900 dark:text-dark-text">
										{exercise.name}
									</p>
									{exercise.sets_reps && (
										<p className={`mt-0.5 text-xs ${aiTrainerMutedTextClassName}`}>
											{exercise.sets_reps}
										</p>
									)}
									{exercise.notes && (
										<p className={`mt-1 text-xs ${aiTrainerMutedTextClassName}`}>{exercise.notes}</p>
									)}
								</li>
							))}
						</ul>
					</div>
				))}

				{plan.days.length > 1 && (
					<button
						type="button"
						onClick={() => setExpanded((value) => !value)}
						className={`text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline`}
					>
						{expanded ? 'Show less' : `Show all ${plan.days.length} days`}
					</button>
				)}

				{plan.notes && (
					<p className={`text-xs leading-relaxed ${aiTrainerMutedTextClassName}`}>{plan.notes}</p>
				)}
			</div>

			<div
				className={
					embedded
						? 'flex gap-2 pt-3'
						: 'flex gap-2 border-t border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-bg px-4 py-3'
				}
			>
				<button
					type="button"
					onClick={handleCopy}
					className={aiTrainerSecondaryButtonClassName}
				>
					{copied ? 'Copied!' : 'Copy plan'}
				</button>
				{!embedded && (
					<button
						type="button"
						onClick={() => setExpanded(true)}
						className={aiTrainerPrimaryButtonClassName}
					>
						View full plan
					</button>
				)}
			</div>
		</div>
	);
}
